/**
 * ReadinessAssessmentPage — /career-transition/assessment
 *
 * The 15-question wizard. Chunked into 4 steps by category so users don't
 * face a wall of 15 questions at once. On submit, calls the backend roadmap
 * generator and navigates to /career-transition/roadmap.
 *
 * Protected route — requires login. Free tier is limited to 1 lifetime
 * generation (enforced server-side). If they've hit the limit, the backend
 * returns 429 and we surface an upsell card instead of blocking submission
 * silently.
 */

import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ASSESSMENT_QUESTIONS, AssessmentQuestion } from "../data/careerTransition";
import { L } from "../utils/localized";
import { generateRoadmap, getLatestRoadmap, isTransitFailure, pollRoadmapJob } from "../api/client";
import { useAuth } from "../hooks/useAuth";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// Group questions by category so we can render them as 4 wizard steps.
// STEPS is a factory so titles/subtitles can be translated — call
// getSteps(t) inside the component, which has a translation hook.
function getSteps(t: TFunction) {
  return [
    { key: "current_state", title: t("ReadinessAssessmentPage.stepCurrentStateTitle"), subtitle: t("ReadinessAssessmentPage.stepCurrentStateSubtitle") },
    { key: "target_state", title: t("ReadinessAssessmentPage.stepTargetStateTitle"), subtitle: t("ReadinessAssessmentPage.stepTargetStateSubtitle") },
    { key: "constraints", title: t("ReadinessAssessmentPage.stepConstraintsTitle"), subtitle: t("ReadinessAssessmentPage.stepConstraintsSubtitle") },
    { key: "preferences", title: t("ReadinessAssessmentPage.stepPreferencesTitle"), subtitle: t("ReadinessAssessmentPage.stepPreferencesSubtitle") },
  ] as const;
}

type AnswerMap = Record<string, any>;

export type SubmitErrorAction = {
  kind: "auth" | "surface" | "recover";
  status?: number;
  message: string;
};

// Classify a submit/poll failure into the ONE action the page takes.
// Extracted for unit testing — structure from the superseded #262, semantics
// from #264: recovery is allowed only for transit-class failures (no HTTP
// answer, or 5xx — see isTransitFailure), never for a definitive 4xx, which
// would mask a quota/auth error behind a stale roadmap.
//
// Takes `t` as a param (rather than calling useTranslation() itself) because
// this is a plain function, not a component/hook — same pattern already
// established for src/utils/heroCtas.ts (I18N_PLAN.md Phase 2.2).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function classifySubmitError(err: any, t: TFunction): SubmitErrorAction {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 401) {
    return {
      kind: "auth",
      status: 401,
      message: t("ReadinessAssessmentPage.sessionExpiredError"),
    };
  }
  if (status === 429) {
    // in_flight (backend ≥ #263): the 429 was caused by a generation still
    // running — not a consumed allocation. The "you've used your allocation,
    // upgrade" upsell to a user whose roadmap is mid-generation was the
    // false-upsell bug.
    const inFlight = typeof detail === "object" && detail?.in_flight === true;
    return {
      kind: "surface",
      status: 429,
      message: inFlight
        ? t("ReadinessAssessmentPage.generationInFlightError")
        : typeof detail === "object" && detail?.message
          ? detail.message
          : t("ReadinessAssessmentPage.quotaExceededError"),
    };
  }
  if (!isTransitFailure(status)) {
    return {
      kind: "surface",
      status,
      message:
        typeof detail === "string"
          ? detail
          : t("ReadinessAssessmentPage.genericSubmitErrorShort"),
    };
  }
  return {
    kind: "recover",
    status,
    message: t("ReadinessAssessmentPage.genericSubmitError"),
  };
}

export default function ReadinessAssessmentPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation("pages-account");
  const STEPS = getSteps(t);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<{ status?: number; message: string } | null>(null);
  const errorAlertRef = useRef<HTMLDivElement | null>(null);

  // Scroll the document to the top whenever the user advances to a new step.
  // useEffect runs AFTER React commits the new step's DOM, which guarantees
  // the new questions are visible from the top — using inline window.scrollTo
  // inside the handler can race against React's render and leave the user
  // viewing the old scroll position with the new content below the fold.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [stepIndex]);

  // When an error appears after submit, scroll it into view so users don't
  // have to hunt for it below the fold.
  useEffect(() => {
    if (submitError && errorAlertRef.current) {
      errorAlertRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [submitError]);

  const currentStep = STEPS[stepIndex];
  const isFinalStep = stepIndex === STEPS.length - 1;
  const questionsForStep = useMemo(
    () => L(ASSESSMENT_QUESTIONS, i18n.language).filter((q) => q.category === currentStep.key),
    [currentStep.key, i18n.language],
  );

  const progress = ((stepIndex + 1) / STEPS.length) * 100;

  const setAnswer = (id: string, value: any) => setAnswers((prev) => ({ ...prev, [id]: value }));

  const isQuestionAnswered = (q: AssessmentQuestion, val: any): boolean => {
    if (!q.required) return true;
    if (q.type === "skill_matrix") {
      if (!val || typeof val !== "object") return false;
      return q.skillRows?.every((row) => typeof val[row.key] === "number") ?? false;
    }
    if (q.type === "multi_select") return Array.isArray(val) && val.length > 0;
    if (q.type === "slider") return typeof val === "number";
    return val !== undefined && val !== "" && val !== null;
  };

  const missingAnswers = useMemo(
    () => questionsForStep.filter((q) => !isQuestionAnswered(q, answers[q.id])),
    [answers, questionsForStep],
  );

  const stepIsValid = missingAnswers.length === 0;

  const handleNext = async () => {
    if (!stepIsValid) return;
    if (stepIndex < STEPS.length - 1) {
      setStepIndex((i) => i + 1);
      return;
    }
    // Final step — submit to backend
    await handleSubmit();
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      // Redirect to login with return-to param so they come back after auth.
      navigate(`/login?redirect=${encodeURIComponent("/career-transition/assessment")}`);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    // Anchor for post-failure recovery: only a roadmap created during THIS
    // attempt may be surfaced as its result (60s tolerance for client/server
    // clock skew). "Any roadmap < 10 min old" silently showed a Pro user
    // their PREVIOUS roadmap after a failed regenerate.
    const attemptStartedAt = Date.now() - 60_000;
    try {
      // 202 + background job: the reservation is made, generation runs
      // server-side. Poll until it resolves — the browser connection no
      // longer has to survive the ~90s generation.
      const accepted = await generateRoadmap(answers);
      const job = await pollRoadmapJob(accepted.job_id);
      if (job.status === "complete" && job.roadmap_id) {
        navigate(`/career-transition/roadmap/${job.roadmap_id}`);
        return;
      }
      setSubmitError({
        status: 502,
        message: t("ReadinessAssessmentPage.generationFailedError"),
      });
      return;
    } catch (err: any) {
      const action = classifySubmitError(err, t);
      // Recovery: the failure had no definitive backend answer, but the
      // roadmap may have finished server-side (the 2026-07-08 incident
      // class). Only a roadmap created during THIS attempt may be surfaced
      // as its result — "anything < 10 min old" showed a stale roadmap.
      if (action.kind === "recover") {
        try {
          const latest = await getLatestRoadmap();
          if (new Date(latest.created_at).getTime() >= attemptStartedAt) {
            navigate(`/career-transition/roadmap/${latest.id}`);
            return;
          }
        } catch {
          // no roadmap — fall through to surfacing the error
        }
      }
      setSubmitError({ status: action.status, message: action.message });
      if (action.kind === "auth") {
        setTimeout(() => {
          navigate(`/login?redirect=${encodeURIComponent("/career-transition/assessment")}`);
        }, 2000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 4, md: 5 },
        }}
      >
        <Container maxWidth="md">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/career-transition")}
            sx={{ color: theme.palette.primary.main, mb: 1.5 }}
          >
            {t("ReadinessAssessmentPage.backToCareerTransitionButton")}
          </Button>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <AutoAwesomeIcon sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="caption"
              fontWeight={700}
              sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              {t("ReadinessAssessmentPage.eyebrow")}
            </Typography>
          </Stack>
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1 }}>
            {t("ReadinessAssessmentPage.stepHeading", { current: stepIndex + 1, total: STEPS.length, title: currentStep.title })}
          </Typography>
          <Typography variant="h6" color="text.secondary" fontWeight={400} sx={{ mb: 2 }}>
            {currentStep.subtitle}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              "& .MuiLinearProgress-bar": { bgcolor: theme.palette.primary.light },
            }}
          />
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 5 } }}>
        <Stack spacing={3}>
          {questionsForStep.map((q) => (
            <QuestionCard
              key={q.id}
              question={q}
              value={answers[q.id]}
              onChange={(v) => setAnswer(q.id, v)}
            />
          ))}
        </Stack>

        {submitError && (
          <Box ref={errorAlertRef}>
            <Alert
              severity={submitError.status === 429 ? "warning" : "error"}
              sx={{ mt: 3, fontSize: 15, alignItems: "center" }}
              action={
                submitError.status === 429 ? (
                  <Button color="inherit" size="small" onClick={() => navigate("/pricing")}>
                    {t("ReadinessAssessmentPage.seePlansButton")}
                  </Button>
                ) : null
              }
            >
              {submitError.message}
            </Alert>
          </Box>
        )}

        <Stack
          direction={isFinalStep ? "column" : "row"}
          spacing={isFinalStep ? 2 : 0}
          justifyContent="space-between"
          alignItems={isFinalStep ? "stretch" : "center"}
          mt={4}
        >
          {!isFinalStep && (
            <Button
              onClick={handleBack}
              disabled={stepIndex === 0 || submitting}
              startIcon={<ArrowBackIcon />}
              sx={{ color: "text.secondary" }}
            >
              {t("ReadinessAssessmentPage.backButton")}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!stepIsValid || submitting}
            endIcon={
              isFinalStep ? null : <ArrowForwardIcon />
            }
            startIcon={
              isFinalStep && !submitting ? <AutoAwesomeIcon sx={{ fontSize: 24 }} /> : null
            }
            size={isFinalStep ? "large" : "medium"}
            fullWidth={isFinalStep}
            sx={{
              bgcolor: theme.palette.primary.main,
              fontWeight: 500,
              ...(isFinalStep
                ? {
                    py: 2.25,
                    px: 5,
                    fontSize: "1.15rem",
                    letterSpacing: "0.01em",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.85)} 100%)`,
                    boxShadow: `0 8px 24px -8px ${alpha(theme.palette.primary.main, 0.6)}, 0 4px 12px -4px ${alpha(theme.palette.primary.main, 0.4)}`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.78)} 100%)`,
                      boxShadow: `0 10px 32px -8px ${alpha(theme.palette.primary.main, 0.7)}, 0 6px 16px -4px ${alpha(theme.palette.primary.main, 0.5)}`,
                      transform: "translateY(-1px)",
                    },
                    "&:active": { transform: "translateY(0)" },
                    "&.Mui-disabled": {
                      background: alpha(theme.palette.primary.main, 0.4),
                      color: "white",
                      boxShadow: "none",
                    },
                    transition: "all 0.2s ease",
                  }
                : {
                    px: 3,
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                  }),
            }}
          >
            {submitting && isFinalStep
              ? t("ReadinessAssessmentPage.generatingRoadmapButton")
              : isFinalStep
              ? t("ReadinessAssessmentPage.generateRoadmapButton")
              : t("ReadinessAssessmentPage.nextButton")}
          </Button>
        </Stack>

        {!stepIsValid && missingAnswers.length > 0 && !submitting && (
          <Alert severity="info" sx={{ mt: 2, fontSize: 14 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {t("ReadinessAssessmentPage.missingAnswersHint", { count: missingAnswers.length })}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {missingAnswers.map((q) => (
                <li key={q.id}>
                  <Typography variant="body2">{q.prompt}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {submitting && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <LinearProgress
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                "& .MuiLinearProgress-bar": { bgcolor: theme.palette.primary.light },
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
              {t("ReadinessAssessmentPage.draftingCaption")}
            </Typography>
          </Box>
        )}
      </Container>

      {/* Full-screen overlay during submit so the loading state is impossible
          to miss — replaces the previous easy-to-overlook inline button text
          change. Click outside is blocked so users can't accidentally
          double-submit or navigate away mid-generation. */}
      <Backdrop
        open={submitting}
        sx={{
          color: theme.palette.background.paper,
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          bgcolor: alpha(theme.palette.text.primary, 0.65),
          flexDirection: "column",
          gap: 3,
          backdropFilter: "blur(4px)",
        }}
      >
        <CircularProgress size={56} sx={{ color: theme.palette.primary.main }} />
        <Stack spacing={1} alignItems="center" sx={{ maxWidth: 460, textAlign: "center", px: 3 }}>
          <Typography variant="h6" fontWeight={500}>
            {t("ReadinessAssessmentPage.generatingRoadmapButton")}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {t("ReadinessAssessmentPage.backdropBody")}
          </Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Individual question renderer
// ---------------------------------------------------------------------------

function QuestionCard({
  question,
  value,
  onChange,
}: {
  question: AssessmentQuestion;
  value: any;
  onChange: (v: any) => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" mb={1}>
        <Typography variant="h6" fontWeight={500} sx={{ flex: 1, lineHeight: 1.4 }}>
          {question.prompt}
        </Typography>
        {question.required && (
          <Chip
            label={t("ReadinessAssessmentPage.requiredChip")}
            size="small"
            sx={{ fontSize: "0.58rem", height: 18, bgcolor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, fontWeight: 500 }}
          />
        )}
      </Stack>
      {question.helpText && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: "italic" }}>
          {question.helpText}
        </Typography>
      )}
      <Box sx={{ mt: 2 }}>
        {question.type === "select" && <SelectInput question={question} value={value} onChange={onChange} />}
        {question.type === "multi_select" && (
          <MultiSelectInput question={question} value={value} onChange={onChange} />
        )}
        {question.type === "slider" && <SliderInput question={question} value={value} onChange={onChange} />}
        {question.type === "skill_matrix" && (
          <SkillMatrixInput question={question} value={value} onChange={onChange} />
        )}
        {question.type === "text" && (
          <Typography variant="caption" color="error">
            {t("ReadinessAssessmentPage.textInputUnsupportedNote")}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

function SelectInput({ question, value, onChange }: InputProps) {
  const theme = useTheme();
  return (
    <RadioGroup value={value || ""} onChange={(e) => onChange(e.target.value)}>
      <Stack spacing={1}>
        {question.options?.map((opt) => (
          <Paper
            key={opt.value}
            onClick={() => onChange(opt.value)}
            sx={{
              p: 1.5,
              border: `1px solid ${value === opt.value ? theme.palette.primary.main : theme.palette.divider}`,
              bgcolor: value === opt.value ? alpha(theme.palette.primary.main, 0.06) : "transparent",
              borderRadius: 2,
              cursor: "pointer",
              transition: "all 0.1s",
              "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.4) },
            }}
          >
            <FormControlLabel
              value={opt.value}
              control={
                <Radio
                  sx={{
                    color: alpha(theme.palette.primary.main, 0.5),
                    "&.Mui-checked": { color: theme.palette.primary.main },
                  }}
                />
              }
              label={opt.label}
              sx={{ width: "100%", m: 0 }}
            />
          </Paper>
        ))}
      </Stack>
    </RadioGroup>
  );
}

function MultiSelectInput({ question, value, onChange }: InputProps) {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  const selected: string[] = Array.isArray(value) ? value : [];
  const toggle = (v: string) => {
    if (selected.includes(v)) onChange(selected.filter((x) => x !== v));
    else onChange([...selected, v]);
  };
  return (
    <Stack spacing={1}>
      {question.options?.map((opt) => {
        const isSelected = selected.includes(opt.value);
        return (
          <Paper
            key={opt.value}
            onClick={() => toggle(opt.value)}
            sx={{
              p: 1.5,
              border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
              bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.06) : "transparent",
              borderRadius: 2,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              transition: "all 0.1s",
              "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.4) },
            }}
          >
            <Box
              sx={{
                width: 18,
                height: 18,
                borderRadius: 0.5,
                border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                bgcolor: isSelected ? theme.palette.primary.main : "transparent",
                color: theme.palette.background.paper,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {isSelected ? "✓" : ""}
            </Box>
            <Typography variant="body2">{opt.label}</Typography>
          </Paper>
        );
      })}
      <Typography variant="caption" color="text.secondary">
        {t("ReadinessAssessmentPage.pickAllThatApply")}
      </Typography>
    </Stack>
  );
}

function SliderInput({ question, value, onChange }: InputProps) {
  const theme = useTheme();
  const defaultVal = Math.round((question.scaleMax! + question.scaleMin!) / 2);
  const val = typeof value === "number" ? value : defaultVal;

  // Auto-persist the visual default on mount when no value is set yet.
  // Without this, the slider visually shows the midpoint (with value label
  // "3" displayed on the handle) but the answers state stays undefined —
  // users reasonably think "yes, my answer is 3" and never touch the
  // slider, then can't submit because validation sees undefined. Treating
  // the visible default as the user's committed answer makes the visual
  // and actual state match.
  useEffect(() => {
    if (typeof value !== "number") {
      onChange(defaultVal);
    }
    // Intentionally only on mount — don't re-fire if onChange identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ px: 1 }}>
      <Slider
        value={val}
        onChange={(_, v) => onChange(v)}
        min={question.scaleMin}
        max={question.scaleMax}
        step={1}
        marks
        valueLabelDisplay="on"
        sx={{
          color: theme.palette.primary.main,
          "& .MuiSlider-rail": { bgcolor: alpha(theme.palette.primary.main, 0.2) },
        }}
      />
      <Stack direction="row" justifyContent="space-between" mt={1}>
        <Typography variant="caption" color="text.secondary">
          {question.scaleLabels?.[0]}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {question.scaleLabels?.[1]}
        </Typography>
      </Stack>
    </Box>
  );
}

function SkillMatrixInput({ question, value, onChange }: InputProps) {
  const theme = useTheme();
  const { t } = useTranslation("pages-account");
  const matrix: Record<string, number> = value && typeof value === "object" ? value : {};
  const set = (key: string, rating: number) => onChange({ ...matrix, [key]: rating });
  return (
    <Stack spacing={1}>
      {question.skillRows?.map((row) => (
        <Paper
          key={row.key}
          sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500}>
                {row.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: "block" }}>
                {row.definition}
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={matrix[row.key] ?? ""}
                onChange={(e) => set(row.key, Number(e.target.value))}
                displayEmpty
                sx={{
                  bgcolor: matrix[row.key] !== undefined ? alpha(theme.palette.primary.main, 0.04) : undefined,
                  "& fieldset": {
                    borderColor: matrix[row.key] !== undefined ? alpha(theme.palette.primary.main, 0.4) : undefined,
                  },
                }}
              >
                <MenuItem value="" disabled>
                  {t("ReadinessAssessmentPage.rate0To5")}
                </MenuItem>
                {[0, 1, 2, 3, 4, 5].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n} — {ratingLabel(t, n)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}

function ratingLabel(t: TFunction, n: number): string {
  const keys = {
    0: "ReadinessAssessmentPage.ratingNoExposure",
    1: "ReadinessAssessmentPage.ratingHeardOfIt",
    2: "ReadinessAssessmentPage.ratingSomeReading",
    3: "ReadinessAssessmentPage.ratingHandsOn",
    4: "ReadinessAssessmentPage.ratingShipped",
    5: "ReadinessAssessmentPage.ratingCouldTeach",
  } as const;
  return t(keys[n as 0 | 1 | 2 | 3 | 4 | 5]);
}

interface InputProps {
  question: AssessmentQuestion;
  value: any;
  onChange: (v: any) => void;
}
